import React, { useRef, useState } from 'react';
import { extractMandateFromPdf } from '../../api/saleMandates';
import { SaleMandate } from '../../types';

interface Props {
  onSuccess: (mandate: SaleMandate) => void;
  onCancel: () => void;
}

export function ExposeUpload({ onSuccess, onCancel }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = (f: File) => {
    if (f.type !== 'application/pdf') { setError('Nur PDF-Dateien sind erlaubt.'); return; }
    setFile(f);
    setError(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleSubmit = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const res = await extractMandateFromPdf(file);
      onSuccess(res.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Upload fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border2)'}`,
          borderRadius: 'var(--radius-lg)', padding: '40px 24px', textAlign: 'center',
          cursor: 'pointer', background: dragging ? 'var(--accent-light)' : 'var(--surface2)',
          transition: 'all 0.15s', marginBottom: '16px',
        }}
      >
        <input ref={fileInputRef} type="file" accept="application/pdf" style={{ display: 'none' }}
          onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }} />
        <div style={{ fontSize: '32px', marginBottom: '8px' }}>📄</div>
        {file ? (
          <div style={{ fontSize: '13px', color: 'var(--text)' }}>
            <strong>{file.name}</strong>
            <div style={{ color: 'var(--text3)', marginTop: '4px' }}>{(file.size / 1024).toFixed(0)} KB</div>
          </div>
        ) : (
          <div style={{ fontSize: '13px', color: 'var(--text2)' }}>
            Exposé-PDF hier ablegen oder klicken
            <div style={{ color: 'var(--text3)', fontSize: '12px', marginTop: '4px' }}>Max. 20 MB</div>
          </div>
        )}
      </div>

      {error && (
        <div style={{
          background: 'var(--accent-light)', border: '1px solid var(--accent)',
          borderRadius: 'var(--radius)', padding: '10px 14px', color: 'var(--accent)',
          fontSize: '13px', marginBottom: '12px',
        }}>
          {error}
        </div>
      )}

      {loading && (
        <div style={{
          background: 'var(--info-bg)', border: '1px solid var(--info)',
          borderRadius: 'var(--radius)', padding: '12px 16px', color: 'var(--info)',
          fontSize: '13px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
          KI analysiert Exposé...
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onCancel} disabled={loading}>Abbrechen</button>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={!file || loading}>
          {loading ? 'Analysiere...' : 'KI-Extraktion starten'}
        </button>
      </div>
    </div>
  );
}
